/**
 * Manages a responsive promotional message with a tooltip/dialog (E20-1217).
 *
 * The whole message bar is the trigger, and click/tap toggles it on every breakpoint —
 * no hover. Unifying the platforms was a design call (supersedes E20-1017): hover opened
 * accidentally on desktop and covered the content underneath. Dismisses on Escape, on the
 * mobile close button, or on a click anywhere outside the bar and the tooltip.
 *
 * Presentation still differs by breakpoint: a bottom sheet on mobile, a floating tooltip
 * positioned against the bar on desktop (mounted on <body> to escape overflow contexts).
 */

if (!customElements.get('promo-message')) {
  // ─── Global State & Listeners ────────────────────────────────────────────────
  // Shared across all instances to prevent O(n) memory leaks on collection pages

  let activePromoInstance = null;
  let globalListenersAttached = false;
  let resizeDebounceTimer = null;

  const handleGlobalClick = (e) => {
    if (!activePromoInstance) return;
    // The container, not just the icon — the whole bar is the trigger, so a click on it
    // is the bar's own toggle rather than an outside dismiss.
    const { container, tooltip } = activePromoInstance;

    if (!container.contains(e.target) && !tooltip.contains(e.target)) {
      activePromoInstance.closeTooltip();
    }
  };

  const handleGlobalKeydown = (e) => {
    if (e.key === 'Escape' && activePromoInstance) {
      const instance = activePromoInstance;
      instance.closeTooltip();
      instance.trigger.focus();
    }
  };

  const handleGlobalResize = () => {
    clearTimeout(resizeDebounceTimer);
    resizeDebounceTimer = setTimeout(() => {
      if (activePromoInstance) activePromoInstance.handleResize();
    }, 100);
  };

  const initGlobalListeners = () => {
    if (globalListenersAttached) return;
    document.addEventListener('click', handleGlobalClick);
    document.addEventListener('touchstart', handleGlobalClick, { passive: true });
    document.addEventListener('keydown', handleGlobalKeydown);
    window.addEventListener('resize', handleGlobalResize);
    globalListenersAttached = true;
  };

  // ─── Custom Element Definition ───────────────────────────────────────────────

  customElements.define(
    'promo-message',
    class extends HTMLElement {
      connectedCallback() {
        this.container = this.querySelector('.product-promo-message');
        this.trigger = this.querySelector('.product-promo-info-icon');
        this.tooltip = this.querySelector('.product-promo-info-tooltip');
        this.closeBtn = this.querySelector('.promo-tooltip-close-mobile');

        if (!this.trigger || !this.tooltip || !this.container) return;

        this.tooltipMounted = false;
        this.isOpen = false;
        this.closeTimer = null;
        this.lastOpenedOnDesktop = null;

        this.tooltip.style.display = 'none';

        // Drop any listeners from a previous connect before rebinding. connectedCallback
        // fires again whenever the theme editor re-renders the section, and a second set
        // of handlers would toggle the tooltip twice per click.
        this.controller?.abort();
        this.controller = new AbortController();

        initGlobalListeners();
        this.bindEvents();
      }

      disconnectedCallback() {
        clearTimeout(this.closeTimer);
        this.controller?.abort();

        if (this.tooltip && this.tooltip.parentNode === document.body) {
          document.body.removeChild(this.tooltip);
        }

        if (activePromoInstance === this) activePromoInstance = null;
      }

      bindEvents() {
        // ── Click Toggle ───────────────────────────────────────────────────────
        // Bound to the container, so the message text is as clickable as the icon. The
        // icon button stays the accessible control — its click bubbles to here, so
        // Enter/Space on it toggles through this one handler rather than a second one.

        // The click is deliberately left to propagate. handleGlobalClick already treats
        // anything inside the container as "not outside", so nothing here needs stopping
        // — and swallowing the event would keep other delegated document handlers from
        // seeing it, most visibly the mega menu's outside-click close.
        this.container.addEventListener(
          'click',
          () => {
            this.isOpen ? this.closeTooltip() : this.openTooltip();
          },
          { signal: this.controller.signal }
        );

        // ── Close Triggers ─────────────────────────────────────────────────────

        // Same reasoning as above, and bfx-button-close already renders type='button',
        // so there's no default action to prevent either.
        if (this.closeBtn) {
          this.closeBtn.addEventListener(
            'click',
            () => {
              this.closeTooltip();
            },
            { signal: this.controller.signal }
          );
        }
      }

      handleResize() {
        if (!this.isOpen || !this.tooltipMounted) return;

        const isDesktop = window.innerWidth >= 768;
        const crossedBreakpoint =
          this.lastOpenedOnDesktop !== null && this.lastOpenedOnDesktop !== isDesktop;

        if (crossedBreakpoint) {
          this.tooltip.style.top = '';
          this.tooltip.style.left = '';
          this.tooltip.style.width = '';
          this.tooltip.classList.remove('align-left', 'align-right');
          this.closeTooltip();
          return;
        }

        if (isDesktop) {
          this.positionDesktopTooltip();
        } else {
          this.tooltip.style.top = '';
          this.tooltip.style.left = '';
          this.tooltip.classList.remove('align-left', 'align-right');
        }
      }

      openTooltip() {
        if (activePromoInstance && activePromoInstance !== this) {
          activePromoInstance.closeTooltip();
        }

        if (!this.tooltipMounted) {
          document.body.appendChild(this.tooltip);
          this.tooltipMounted = true;
        }

        clearTimeout(this.closeTimer);
        this.tooltip.style.display = '';
        this.lastOpenedOnDesktop = window.innerWidth >= 768;
        activePromoInstance = this;
        this.isOpen = true;
        this.tooltip.classList.add('is-active');
        this.tooltip.setAttribute('aria-hidden', 'false');
        this.trigger.setAttribute('aria-expanded', 'true');

        if (window.innerWidth >= 768) {
          this.positionDesktopTooltip();
        } else {
          this.tooltip.style.top = '';
          this.tooltip.style.left = '';
        }
      }

      closeTooltip() {
        clearTimeout(this.closeTimer);

        this.isOpen = false;
        this.lastOpenedOnDesktop = null;
        this.tooltip.classList.remove('is-active');
        this.tooltip.setAttribute('aria-hidden', 'true');
        this.trigger.setAttribute('aria-expanded', 'false');

        // Wait for CSS transition to finish before hiding from layout
        this.closeTimer = setTimeout(() => {
          if (!this.isOpen) this.tooltip.style.display = 'none';
        }, 300);

        if (activePromoInstance === this) activePromoInstance = null;
      }

      positionDesktopTooltip() {
        const iconRect = this.trigger.getBoundingClientRect();
        const containerRect = this.container.getBoundingClientRect();
        const viewportWidth = window.innerWidth;

        const top = containerRect.bottom + window.scrollY + 12;
        const left = iconRect.left + window.scrollX + iconRect.width / 2;

        this.tooltip.style.top = `${top}px`;
        this.tooltip.style.left = `${left}px`;

        this.tooltip.classList.remove('align-left', 'align-right');
        const iconCenter = iconRect.left + iconRect.width / 2;
        const halfWidth = this.tooltip.offsetWidth / 2;
        if (iconCenter + halfWidth > viewportWidth - 16) {
          this.tooltip.classList.add('align-right');
        } else if (iconCenter - halfWidth < 16) {
          this.tooltip.classList.add('align-left');
        }
      }
    }
  );
}
